import {
  LoaderFunction,
  LoaderFunctionArgs,
  useFetcher,
} from "react-router-dom";
import { getSearchQuery } from "../query.ts";
import { searchBookContent } from "../../../../common/api/decimal/methods/searchBookContent.ts";
import { escapeRegExp, excerptText } from "../../../../common/utils/string.ts";
import { DeepLinkType, getReaderDeepLink } from "../../../bookshelfLinks.ts";
import { noticeError } from "../../../../common/api/monitoring";

interface ContentResultItem {
  text: string;
  page: string;
  deepLink: string;
}

interface ContentResults {
  sections: { title: string; items: ContentResultItem[] }[];
}

export const searchContent: LoaderFunction = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const query = getSearchQuery(url.searchParams);
  const vbid = params.vbid;

  if (!query || !vbid) {
    return { sections: [] };
  }

  const response = await searchBookContent(query, vbid);
  const data = response.data;
  if (!data?.locations?.length) {
    return { sections: [] };
  }
  const results: ContentResults = { sections: [] };
  data.locations.forEach((loc) => {
    // This is not expected to occur, but it does and we need specific vbids to
    // be able to identify why
    if (loc.term_locs === undefined) {
      noticeError("term_locs is undefined", { vbid: data.vbid, query });
      return;
    }

    let items = results.sections.find(
      (section) => section.title === loc.chapter_title
    )?.items;
    if (!items) {
      items = [];
      results.sections.push({ title: loc.chapter_title, items });
    }
    const page = loc.page ?? "";
    const deepLink = loc.cfi
      ? getReaderDeepLink(loc.cfi, DeepLinkType.Cfi)
      : getReaderDeepLink(String(loc.id), DeepLinkType.PageId);

    for (const termLoc of loc.term_locs) {
      const queryToHighlight = loc.text.substring(
        termLoc.pos,
        termLoc.pos + termLoc.len
      );
      const regex = new RegExp(`(${escapeRegExp(queryToHighlight)})`, "ig");
      let text = excerptText(loc.text, queryToHighlight, {
        startPos: termLoc.pos,
      });
      text = text.replace(regex, "<strong>$1</strong>");
      items.push({ text, page, deepLink });
    }
  });

  return results;
};

export const useContentResultsFetcher = () => {
  return useFetcher<ContentResults>();
};
